/**
 * XR Assistant Panel
 *
 * A voice-driven AI assistant panel for immersive WebXR sessions.
 * Shows as a small floating AI icon pinned to the upper-right of the user's
 * view. Single tap toggles recording; long press opens/closes the conversation
 * panel. The icon pulses/glows to reflect state (recording, thinking, etc.).
 *
 * Uses MediaRecorder (not Web Speech API, which is broken in immersive WebXR
 * on Quest) and Gemini's multimodal API for speech-to-text.
 * TTS uses Gemini TTS via Web Audio API (SpeechSynthesis is unavailable in XR).
 *
 * All UI is pure Babylon.js 3D (no DOM).
 */

import { ensureAudioContextRunning, playStartTone, playStopTone } from "./XRPanelAudio";
import { Scene } from "@babylonjs/core/scene";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import "@babylonjs/core/Meshes/Builders/planeBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { PointerEventTypes } from "@babylonjs/core/Events/pointerEvents";
import type { Observer } from "@babylonjs/core/Misc/observable";
import type { PointerInfo } from "@babylonjs/core/Events/pointerEvents";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";

import { AdvancedDynamicTexture } from "@babylonjs/gui/2D/advancedDynamicTexture";
import { TextBlock, TextWrapping } from "@babylonjs/gui/2D/controls/textBlock";
import { Rectangle } from "@babylonjs/gui/2D/controls/rectangle";
import { Ellipse } from "@babylonjs/gui/2D/controls/ellipse";
import { ScrollViewer } from "@babylonjs/gui/2D/controls/scrollViewers/scrollViewer";
import { StackPanel } from "@babylonjs/gui/2D/controls/stackPanel";
import { Control } from "@babylonjs/gui/2D/controls/control";
import { Image } from "@babylonjs/gui/2D/controls/image";

import { agentService } from './AgentService';
import { transcribeAudio } from './transcribe-audio';
import { synthesizeSpeech } from './synthesize-speech';
import { VoiceTiming } from './voice-timing';
import type { TraceScene } from 'trace-viewer/scene';

// ── Layout Constants ──────────────────────────────────────────

/** Distance in meters the icon/panel floats in front of the user */
const FOLLOW_DISTANCE = 0.9;
/** How quickly the icon lerps toward the target position (0–1 per frame) */
const FOLLOW_SPEED = 0.12;
/** Vertical offset from eye level (positive = above center) */
const ICON_VERTICAL_OFFSET = 0.15;
/** Horizontal offset from gaze center (positive = right) */
const ICON_HORIZONTAL_OFFSET = 0.25;

/** Icon disc size (meters) */
const ICON_SIZE = 0.06;
/** Expanded panel size (meters) */
const PANEL_WIDTH = 0.7;
const PANEL_HEIGHT = 0.5;

/** GUI texture resolution for the icon */
const ICON_TEX_SIZE = 128;
/** Inline status label next to icon (meters) */
const LABEL_WIDTH = 0.22;
const LABEL_HEIGHT = 0.035;
const LABEL_TEX_WIDTH = 512;
const LABEL_TEX_HEIGHT = 80;
/** GUI texture resolution for the expanded panel */
const PANEL_TEX_WIDTH = 2048;
const PANEL_TEX_HEIGHT = 1440;

/** Duration (ms) to hold pointer before it counts as a long press */
const LONG_PRESS_MS = 400;

// ── AI icon SVGs ──────────────────────────────────────────────
// Abstract geometric "AI spark" — a 4-pointed starburst, centered in viewBox
// The star fills most of the viewBox so it appears large within the icon circle
const AI_ICON_SVG = `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none">
  <path d="M32 2 L37 24 L58 18 L42 32 L58 46 L37 40 L32 62 L27 40 L6 46 L22 32 L6 18 L27 24 Z" fill="white" opacity="0.95"/>
  <circle cx="32" cy="32" r="4.5" fill="white"/>
</svg>`)}`;

const AI_ICON_ACTIVE_SVG = `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none">
  <path d="M32 2 L37 24 L58 18 L42 32 L58 46 L37 40 L32 62 L27 40 L6 46 L22 32 L6 18 L27 24 Z" fill="#ff9999" opacity="0.95"/>
  <circle cx="32" cy="32" r="4.5" fill="white"/>
</svg>`)}`;

// ── State ─────────────────────────────────────────────────────

type PanelState = 'idle' | 'listening' | 'transcribing' | 'thinking' | 'speaking';

interface ConversationEntry {
    role: 'user' | 'assistant';
    text: string;
}

/**
 * In-VR AI assistant panel.
 *
 * Lifecycle:
 *   const panel = new XRAssistantPanel(scene, traceScene);
 *   panel.show();   // when XR session starts
 *   panel.dispose(); // when XR session ends
 */
export class XRAssistantPanel {
    private readonly _scene: Scene;
    private readonly _traceScene: TraceScene;

    // 3D structure — each element has its own root for independent billboarding
    private _iconRoot: TransformNode;
    private _labelRoot: TransformNode;
    private _panelRoot: TransformNode;
    private _iconMesh: Mesh | null = null;
    private _panelMesh: Mesh | null = null;
    private _labelMesh: Mesh | null = null;

    // GUI textures
    private _iconTexture: AdvancedDynamicTexture | null = null;
    private _panelTexture: AdvancedDynamicTexture | null = null;
    private _labelTexture: AdvancedDynamicTexture | null = null;

    // GUI controls (for dynamic updates)
    private _iconBg: Ellipse | null = null;
    private _iconImage: Image | null = null;
    private _labelText: TextBlock | null = null;
    private _statusText: TextBlock | null = null;
    private _conversationStack: StackPanel | null = null;
    private _scrollViewer: ScrollViewer | null = null;
    private _interimText: TextBlock | null = null;

    // State
    private _state: PanelState = 'idle';
    private _expanded = false;
    private _conversation: ConversationEntry[] = [];

    // Audio recording (MediaRecorder — works in immersive WebXR unlike SpeechRecognition)
    private _mediaStream: MediaStream | null = null;
    private _mediaRecorder: MediaRecorder | null = null;
    private _audioChunks: Blob[] = [];
    private _recordingStartTime = 0;

    // TTS + confirmation tones (Web Audio — works in immersive XR)
    private _audioContext: AudioContext | null = null;
    private _ttsAbort: AbortController | null = null;

    // Observers
    private _renderObserver: Observer<Scene> | null = null;
    private _pointerObserver: Observer<PointerInfo> | null = null;

    // Long-press detection
    private _pointerDownTime = 0;
    private _pointerDownMesh: Mesh | null = null;
    private _longPressTimer: ReturnType<typeof setTimeout> | null = null;
    private _longPressHandled = false;

    // Suppress tap after close button click (prevents re-triggering record)
    private _suppressNextTap = false;

    // Pulsing glow animation
    private _pulsePhase = 0;

    // Follow targets (smoothed)
    private _iconTargetPosition = new Vector3();
    private _labelTargetPosition = new Vector3();
    private _panelTargetPosition = new Vector3();
    private _initialized = false;

    constructor(scene: Scene, traceScene: TraceScene) {
        this._scene = scene;
        this._traceScene = traceScene;

        this._iconRoot = new TransformNode("xr-assistant-icon-root", scene);
        this._labelRoot = new TransformNode("xr-assistant-label-root", scene);
        this._panelRoot = new TransformNode("xr-assistant-panel-root", scene);

        // AudioContext for TTS playback + confirmation tones
        this._audioContext = new AudioContext();

        this._buildIcon();
        this._buildLabel();
        this._buildPanel();
        this._setupInteraction();
        this._setupFollowBehavior();

        // Mic is acquired lazily on first recording to avoid prompting for
        // permission in sessions where the assistant is never used.
    }

    // ── Public API ────────────────────────────────────────────

    show(): void {
        this._iconRoot.setEnabled(true);
        if (this._expanded) {
            this._panelRoot.setEnabled(true);
        }
    }

    hide(): void {
        this._iconRoot.setEnabled(false);
        this._panelRoot.setEnabled(false);
        this._stopRecording();
    }

    dispose(): void {
        this._stopRecording();
        this._cancelSpeech();
        this._releaseMic();
        this._clearLongPressTimer();

        if (this._audioContext) {
            void this._audioContext.close();
            this._audioContext = null;
        }

        if (this._renderObserver) {
            this._scene.onBeforeRenderObservable.remove(this._renderObserver);
            this._renderObserver = null;
        }
        if (this._pointerObserver) {
            this._scene.onPointerObservable.remove(this._pointerObserver);
            this._pointerObserver = null;
        }

        this._iconTexture?.dispose();
        this._labelTexture?.dispose();
        this._panelTexture?.dispose();
        this._iconMesh?.dispose();
        this._labelMesh?.dispose();
        this._panelMesh?.dispose();
        this._iconRoot.dispose();
        this._labelRoot.dispose();
        this._panelRoot.dispose();
    }

    // ── Mic Access ────────────────────────────────────────────

    private async _acquireMic(): Promise<void> {
        try {
            this._mediaStream = await navigator.mediaDevices.getUserMedia({
                audio: { echoCancellation: true, noiseSuppression: true },
            });
            console.log('[XRAssistant] Mic acquired');
        } catch (err) {
            console.warn('[XRAssistant] Failed to acquire mic:', err);
        }
    }

    private _releaseMic(): void {
        if (this._mediaStream) {
            for (const track of this._mediaStream.getTracks()) {
                track.stop();
            }
            this._mediaStream = null;
        }
    }

    // Audio helpers live in XRPanelAudio.ts. Thin wrappers below keep the
    // call sites in this file terse.
    private _ensureAudioContextRunning(): void { ensureAudioContextRunning(this._audioContext); }
    private _playStartTone(): void { playStartTone(this._audioContext); }
    private _playStopTone(): void { playStopTone(this._audioContext); }

    // ── Icon ──────────────────────────────────────────────────

    private _buildIcon(): void {
        const mesh = MeshBuilder.CreatePlane("xr-assistant-icon", {
            width: ICON_SIZE,
            height: ICON_SIZE,
        }, this._scene);
        mesh.parent = this._iconRoot;
        mesh.isPickable = true;

        const mat = new StandardMaterial("xr-assistant-icon-mat", this._scene);
        mat.disableLighting = true;
        mat.emissiveColor = new Color3(1, 1, 1);
        mat.backFaceCulling = false;
        mesh.material = mat;

        const adt = AdvancedDynamicTexture.CreateForMesh(
            mesh, ICON_TEX_SIZE, ICON_TEX_SIZE, false, false, undefined, undefined,
            Texture.TRILINEAR_SAMPLINGMODE,
        );
        adt.hasAlpha = true;

        const bg = new Ellipse("icon-bg");
        bg.width = "100px";
        bg.height = "100px";
        bg.color = "transparent";
        bg.background = "rgba(0, 0, 0, 0.7)";
        bg.thickness = 2;
        adt.addControl(bg);

        const icon = new Image("ai-icon", AI_ICON_SVG);
        icon.width = "100px";
        icon.height = "100px";
        bg.addControl(icon);

        this._iconMesh = mesh;
        this._iconTexture = adt;
        this._iconBg = bg;
        this._iconImage = icon;
    }

    // ── Inline Status Label (next to icon) ────────────────────

    private _buildLabel(): void {
        const mesh = MeshBuilder.CreatePlane("xr-assistant-label", {
            width: LABEL_WIDTH,
            height: LABEL_HEIGHT,
        }, this._scene);
        mesh.parent = this._labelRoot;
        mesh.isPickable = false;

        const mat = new StandardMaterial("xr-assistant-label-mat", this._scene);
        mat.disableLighting = true;
        mat.emissiveColor = new Color3(1, 1, 1);
        mat.backFaceCulling = false;
        mesh.material = mat;

        const adt = AdvancedDynamicTexture.CreateForMesh(
            mesh, LABEL_TEX_WIDTH, LABEL_TEX_HEIGHT, false, false, undefined, undefined,
            Texture.TRILINEAR_SAMPLINGMODE,
        );
        adt.hasAlpha = true;

        // Background pill
        const bg = new Rectangle("label-bg");
        bg.width = 1;
        bg.height = 1;
        bg.background = "rgba(10, 10, 15, 0.8)";
        bg.color = "rgba(255, 255, 255, 0.15)";
        bg.thickness = 2;
        bg.cornerRadius = 30;
        adt.addControl(bg);

        const text = new TextBlock("label-text", "");
        text.color = "white";
        text.fontSize = 42;
        text.fontFamily = "system-ui, -apple-system, sans-serif";
        text.fontWeight = "500";
        text.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
        bg.addControl(text);

        // Start hidden
        this._labelRoot.setEnabled(false);

        this._labelMesh = mesh;
        this._labelTexture = adt;
        this._labelText = text;
    }

    // ── Panel (expanded state) ────────────────────────────────

    private _buildPanel(): void {
        const mesh = MeshBuilder.CreatePlane("xr-assistant-panel", {
            width: PANEL_WIDTH,
            height: PANEL_HEIGHT,
        }, this._scene);
        mesh.parent = this._panelRoot;
        mesh.isPickable = true;

        const mat = new StandardMaterial("xr-assistant-panel-mat", this._scene);
        mat.disableLighting = true;
        mat.emissiveColor = new Color3(1, 1, 1);
        mat.backFaceCulling = false;
        mesh.material = mat;

        const adt = AdvancedDynamicTexture.CreateForMesh(
            mesh, PANEL_TEX_WIDTH, PANEL_TEX_HEIGHT, false, false, undefined, undefined,
            Texture.TRILINEAR_SAMPLINGMODE,
        );
        adt.hasAlpha = true;

        // Background
        const bg = new Rectangle("panel-bg");
        bg.width = 1;
        bg.height = 1;
        bg.background = "rgba(10, 10, 15, 0.88)";
        bg.color = "rgba(255, 255, 255, 0.15)";
        bg.thickness = 2;
        bg.cornerRadius = 40;
        adt.addControl(bg);

        // Header
        const header = new Rectangle("header");
        header.width = 1;
        header.height = "140px";
        header.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        header.background = "rgba(255, 255, 255, 0.06)";
        header.color = "transparent";
        header.thickness = 0;
        header.isPointerBlocker = true;
        header.onPointerClickObservable.add(() => {
            this._suppressNextTap = true;
            this._collapse();
        });
        bg.addControl(header);

        const title = new TextBlock("title", "AI Assistant");
        title.color = "white";
        title.fontSize = 64;
        title.fontFamily = "system-ui, -apple-system, sans-serif";
        title.fontWeight = "600";
        title.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        title.paddingLeft = "40px";
        title.paddingRight = "120px";
        header.addControl(title);

        const status = new TextBlock("status", "Tap icon to speak");
        status.color = "rgba(255, 255, 255, 0.5)";
        status.fontSize = 36;
        status.fontFamily = "system-ui, -apple-system, sans-serif";
        status.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        status.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
        status.paddingLeft = "40px";
        status.paddingBottom = "12px";
        status.height = "50px";
        header.addControl(status);
        this._statusText = status;

        // Conversation scroll area
        const scrollViewer = new ScrollViewer("scroll");
        scrollViewer.width = 0.96;
        scrollViewer.height = "1060px";
        scrollViewer.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        scrollViewer.top = "160px";
        scrollViewer.color = "transparent";
        scrollViewer.barColor = "rgba(255, 255, 255, 0.2)";
        scrollViewer.barBackground = "transparent";
        scrollViewer.thickness = 0;
        bg.addControl(scrollViewer);
        this._scrollViewer = scrollViewer;

        const stack = new StackPanel("conversation");
        stack.width = 1;
        stack.isVertical = true;
        stack.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        scrollViewer.addControl(stack);
        this._conversationStack = stack;

        // Interim/status text at bottom
        const interim = new TextBlock("interim", "");
        interim.color = "rgba(255, 255, 255, 0.4)";
        interim.fontSize = 44;
        interim.fontFamily = "system-ui, -apple-system, sans-serif";
        interim.fontStyle = "italic";
        interim.height = "80px";
        interim.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
        interim.paddingBottom = "20px";
        interim.textWrapping = TextWrapping.Ellipsis;
        bg.addControl(interim);
        this._interimText = interim;

        // Close indicator (visual only — the whole header is tappable to close)
        const closeIcon = new TextBlock("close-x", "\u2715");
        closeIcon.color = "rgba(255, 255, 255, 0.4)";
        closeIcon.fontSize = 48;
        closeIcon.width = "80px";
        closeIcon.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
        closeIcon.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        closeIcon.top = "30px";
        closeIcon.left = "-30px";
        header.addControl(closeIcon);

        // Start hidden
        this._panelRoot.setEnabled(false);

        this._panelMesh = mesh;
        this._panelTexture = adt;
    }

    // ── Interaction ───────────────────────────────────────────

    private _setupInteraction(): void {
        this._pointerObserver = this._scene.onPointerObservable.add((info) => {
            const picked = info.pickInfo?.pickedMesh;

            if (info.type === PointerEventTypes.POINTERDOWN) {
                if (picked !== this._iconMesh && picked !== this._panelMesh) return;

                // If the close button just fired, swallow this event
                if (this._suppressNextTap) {
                    this._suppressNextTap = false;
                    return;
                }

                this._pointerDownTime = performance.now();
                this._pointerDownMesh = picked as Mesh;
                this._longPressHandled = false;

                // Start long-press timer for icon only
                if (picked === this._iconMesh) {
                    this._clearLongPressTimer();
                    this._longPressTimer = setTimeout(() => {
                        this._longPressHandled = true;
                        this._onIconLongPress();
                    }, LONG_PRESS_MS);
                }
            } else if (info.type === PointerEventTypes.POINTERUP) {
                this._clearLongPressTimer();

                if (this._suppressNextTap) {
                    this._suppressNextTap = false;
                    this._pointerDownMesh = null;
                    return;
                }

                if (!this._pointerDownMesh) return;
                const downMesh = this._pointerDownMesh;
                this._pointerDownMesh = null;

                // If long press already fired, skip the tap
                if (this._longPressHandled) return;

                const elapsed = performance.now() - this._pointerDownTime;
                if (elapsed >= LONG_PRESS_MS) return;

                if (downMesh === this._iconMesh) {
                    this._onIconTap();
                } else if (downMesh === this._panelMesh) {
                    this._onPanelTap();
                }
            }
        });
    }

    private _clearLongPressTimer(): void {
        if (this._longPressTimer !== null) {
            clearTimeout(this._longPressTimer);
            this._longPressTimer = null;
        }
    }

    /** Single tap on icon: toggle recording (record → submit → record...) */
    private _onIconTap(): void {
        if (this._state === 'listening') {
            this._playStopTone();
            void this._finishRecording();
        } else if (this._state === 'idle' || this._state === 'speaking') {
            this._cancelSpeech();
            this._playStartTone();
            void this._startRecording();
        }
        // Ignore taps during transcribing/thinking
    }

    /** Long press on icon: toggle panel open/closed */
    private _onIconLongPress(): void {
        if (this._expanded) {
            this._collapse();
        } else {
            this._expand();
        }
    }

    /** Tap on the panel body: same as icon tap (record/submit) */
    private _onPanelTap(): void {
        if (this._state === 'idle' || this._state === 'speaking') {
            this._cancelSpeech();
            this._playStartTone();
            void this._startRecording();
        } else if (this._state === 'listening') {
            this._playStopTone();
            void this._finishRecording();
        }
    }

    private _expand(): void {
        this._expanded = true;
        this._panelRoot.setEnabled(true);
    }

    private _collapse(): void {
        this._expanded = false;
        this._panelRoot.setEnabled(false);
        this._stopRecording();
        // Don't cancel speech — let TTS continue playing while minimized
    }

    // ── Follow Behavior ───────────────────────────────────────

    private _setupFollowBehavior(): void {
        this._renderObserver = this._scene.onBeforeRenderObservable.add(() => {
            const camera = this._scene.activeCamera;
            if (!camera) return;

            const forward = camera.getForwardRay().direction;
            const camPos = camera.globalPosition;

            // Compute camera-relative axes
            const worldUp = Vector3.Up();
            const right = Vector3.Cross(forward, worldUp).normalize();
            const camUp = Vector3.Cross(right, forward).normalize();

            // Icon target: offset to upper-right of gaze
            const iconTarget = camPos
                .add(forward.scale(FOLLOW_DISTANCE))
                .add(right.scale(ICON_HORIZONTAL_OFFSET))
                .add(camUp.scale(ICON_VERTICAL_OFFSET));

            // Label target: to the left of the icon
            const labelTarget = iconTarget
                .subtract(right.scale(ICON_SIZE / 2 + LABEL_WIDTH / 2 + 0.01));

            // Panel target: just below the icon/label row
            const panelTarget = iconTarget
                .subtract(camUp.scale(ICON_SIZE / 2 + PANEL_HEIGHT / 2 + 0.015))
                .subtract(right.scale(PANEL_WIDTH / 2 - ICON_SIZE / 2));

            if (!this._initialized) {
                this._iconRoot.position.copyFrom(iconTarget);
                this._iconTargetPosition.copyFrom(iconTarget);
                this._labelRoot.position.copyFrom(labelTarget);
                this._labelTargetPosition.copyFrom(labelTarget);
                this._panelRoot.position.copyFrom(panelTarget);
                this._panelTargetPosition.copyFrom(panelTarget);
                this._initialized = true;
            } else {
                this._iconTargetPosition.copyFrom(iconTarget);
                Vector3.LerpToRef(
                    this._iconRoot.position,
                    this._iconTargetPosition,
                    FOLLOW_SPEED,
                    this._iconRoot.position,
                );

                this._labelTargetPosition.copyFrom(labelTarget);
                Vector3.LerpToRef(
                    this._labelRoot.position,
                    this._labelTargetPosition,
                    FOLLOW_SPEED,
                    this._labelRoot.position,
                );

                this._panelTargetPosition.copyFrom(panelTarget);
                Vector3.LerpToRef(
                    this._panelRoot.position,
                    this._panelTargetPosition,
                    FOLLOW_SPEED,
                    this._panelRoot.position,
                );
            }

            // Billboard: each root faces the camera independently
            this._iconRoot.lookAt(camPos);
            this._labelRoot.lookAt(camPos);
            this._panelRoot.lookAt(camPos);

            // Pulse animation for active states
            this._updatePulse();
        });
    }

    // ── Pulse / Glow Animation ────────────────────────────────

    private _updatePulse(): void {
        if (!this._iconBg || !this._iconMesh || !this._iconImage) return;

        const isActive = this._state === 'listening'
            || this._state === 'transcribing'
            || this._state === 'thinking';

        if (isActive) {
            this._pulsePhase += 0.12;
            const pulse = 0.5 + 0.5 * Math.sin(this._pulsePhase);
            // Heavy border glow
            this._iconBg.thickness = 4 + pulse * 12;
            // Whole icon breathes
            const meshScale = 1.0 + pulse * 0.25;
            this._iconMesh.scaling.setAll(meshScale);
            // Star pulses independently (faster, bigger range)
            const starPulse = 0.5 + 0.5 * Math.sin(this._pulsePhase * 1.5);
            const starScale = 0.7 + starPulse * 0.5;
            this._iconImage.scaleX = starScale;
            this._iconImage.scaleY = starScale;
            // Circle background opacity pulses
            const alpha = 0.6 + pulse * 0.35;
            this._iconBg.background = `rgba(220, 40, 40, ${alpha.toFixed(2)})`;
        } else if (this._state === 'speaking') {
            this._pulsePhase += 0.08;
            const pulse = 0.5 + 0.5 * Math.sin(this._pulsePhase);
            this._iconBg.thickness = 3 + pulse * 8;
            const meshScale = 1.0 + pulse * 0.12;
            this._iconMesh.scaling.setAll(meshScale);
            const starScale = 0.85 + pulse * 0.3;
            this._iconImage.scaleX = starScale;
            this._iconImage.scaleY = starScale;
            const alpha = 0.6 + pulse * 0.3;
            this._iconBg.background = `rgba(40, 180, 80, ${alpha.toFixed(2)})`;
        } else {
            this._pulsePhase = 0;
            this._iconBg.thickness = 2;
            this._iconMesh.scaling.setAll(1.0);
            this._iconImage.scaleX = 1.0;
            this._iconImage.scaleY = 1.0;
        }
    }

    // ── Audio Recording ───────────────────────────────────────

    private async _startRecording(): Promise<void> {
        if (!this._mediaStream) {
            await this._acquireMic();
        }
        if (!this._mediaStream) {
            console.warn('[XRAssistant] No mic stream available');
            if (this._expanded) {
                this._addMessage('assistant', 'Microphone not available. Please grant mic permission and re-enter XR.');
            }
            return;
        }

        this._audioChunks = [];
        this._recordingStartTime = performance.now();

        // Pick a supported MIME type — Quest browser may vary
        const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
            ? 'audio/webm;codecs=opus'
            : MediaRecorder.isTypeSupported('audio/webm')
                ? 'audio/webm'
                : 'audio/mp4';

        try {
            this._mediaRecorder = new MediaRecorder(this._mediaStream, { mimeType });
        } catch {
            this._mediaRecorder = new MediaRecorder(this._mediaStream);
        }

        this._mediaRecorder.ondataavailable = (ev) => {
            if (ev.data.size > 0) {
                this._audioChunks.push(ev.data);
            }
        };

        this._mediaRecorder.start(250);
        this._setState('listening');
        console.log('[XRAssistant] Listening started');

        if (this._interimText) {
            this._interimText.text = 'Listening... tap to send';
        }
    }

    private _stopRecording(): void {
        if (this._mediaRecorder && this._mediaRecorder.state !== 'inactive') {
            try {
                this._mediaRecorder.stop();
            } catch {
                // Already stopped
            }
        }
        this._mediaRecorder = null;
        this._audioChunks = [];
        if (this._interimText) {
            this._interimText.text = '';
        }
    }

    /**
     * Stop recording, assemble audio blob, transcribe via Gemini, then send to AI.
     */
    private async _finishRecording(): Promise<void> {
        if (!this._mediaRecorder || this._mediaRecorder.state === 'inactive') {
            this._setState('idle');
            return;
        }

        const timing = new VoiceTiming('xr-voice');
        timing.mark('recording_stop_tap');
        const durationMs = performance.now() - this._recordingStartTime;
        console.log(`[XRAssistant] Finishing recording (${(durationMs / 1000).toFixed(1)}s)`);

        const recorder = this._mediaRecorder;
        const mimeType = recorder.mimeType;

        const audioBlob = await new Promise<Blob>((resolve) => {
            recorder.ondataavailable = (ev) => {
                if (ev.data.size > 0) {
                    this._audioChunks.push(ev.data);
                }
            };
            recorder.onstop = () => {
                resolve(new Blob(this._audioChunks, { type: mimeType }));
            };
            recorder.stop();
        });
        timing.mark('blob_assembled', { bytes: audioBlob.size, recMs: durationMs.toFixed(0) });

        this._mediaRecorder = null;
        this._audioChunks = [];

        if (audioBlob.size < 1000 || durationMs < 500) {
            console.log('[XRAssistant] Recording too short, ignoring');
            this._setState('idle');
            if (this._interimText) {
                this._interimText.text = '';
            }
            return;
        }

        this._setState('transcribing');
        if (this._interimText) {
            this._interimText.text = 'Transcribing...';
        }

        const transcript = await transcribeAudio(audioBlob, mimeType, timing).catch((err) => {
            console.error('[XRAssistant] Transcription error:', err);
            return null;
        });
        timing.mark('stt_done', { chars: transcript?.length ?? 0 });

        if (this._interimText) {
            this._interimText.text = '';
        }

        if (!transcript) {
            console.log('[XRAssistant] No transcript from audio');
            this._setState('idle');
            timing.summary();
            return;
        }

        console.log('[XRAssistant] Transcript:', transcript);
        void this._handleUserInput(transcript, timing);
    }

    // ── TTS (Gemini TTS via Web Audio) ─────────────────────────

    private _speak(text: string, timing?: VoiceTiming): Promise<void> {
        if (!this._audioContext || !text) return Promise.resolve();

        this._ensureAudioContextRunning();
        this._cancelSpeech();
        const abort = new AbortController();
        this._ttsAbort = abort;

        const onFirstAudio = () => timing?.summary();
        return synthesizeSpeech(text, this._audioContext, abort.signal, timing, onFirstAudio)
            .then(() => {
                if (this._state === 'speaking' && !abort.signal.aborted) {
                    this._setState('idle');
                }
            })
            .catch((err) => {
                if (!abort.signal.aborted) {
                    console.warn('[XRAssistant] TTS error:', err);
                    if (this._state === 'speaking') {
                        this._setState('idle');
                    }
                }
            });
    }

    private _cancelSpeech(): void {
        if (this._ttsAbort) {
            this._ttsAbort.abort();
            this._ttsAbort = null;
        }
    }

    // ── AI Integration ────────────────────────────────────────

    private async _handleUserInput(text: string, timing?: VoiceTiming): Promise<void> {
        if (!text) return;

        this._addMessage('user', text);
        this._setState('thinking');

        if (this._interimText) {
            this._interimText.text = '';
        }

        agentService.setScene(this._traceScene);

        let fullResponse = '';
        try {
            fullResponse = await agentService.sendMessage(
                text,
                (_chunk) => {
                    // Streaming — could update panel incrementally in future
                },
                (toolName, status) => {
                    if (status === 'running') {
                        console.log(`[XRAssistant] Tool: ${toolName} (${status})`);
                        if (this._statusText) {
                            this._statusText.text = `Running ${toolName}...`;
                        }
                    }
                },
            );
            timing?.mark('llm_done', { respChars: fullResponse.length });
            console.log('[XRAssistant] AI response:', fullResponse?.slice(0, 200));
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            console.error('[XRAssistant] AI error:', msg);
            fullResponse = `Error: ${msg}`;
        }

        this._addMessage('assistant', fullResponse || '(no response)');

        if (fullResponse) {
            this._setState('speaking');
            // Summary fires from inside _speak at tts_playback_start — the
            // user-perceived "first audio" latency.
            void this._speak(fullResponse, timing);
        } else {
            this._setState('idle');
            timing?.summary();
        }
    }

    // ── State Management ──────────────────────────────────────

    private _setState(state: PanelState): void {
        this._state = state;
        this._updateVisuals();
    }

    private _updateVisuals(): void {
        if (this._iconBg) {
            switch (this._state) {
                case 'listening':
                    this._iconBg.background = "rgba(220, 40, 40, 0.85)";
                    this._iconBg.color = "rgba(255, 100, 100, 0.9)";
                    break;
                case 'transcribing':
                case 'thinking':
                    this._iconBg.background = "rgba(40, 100, 220, 0.85)";
                    this._iconBg.color = "rgba(100, 160, 255, 0.8)";
                    break;
                case 'speaking':
                    this._iconBg.background = "rgba(40, 180, 80, 0.85)";
                    this._iconBg.color = "rgba(100, 255, 140, 0.8)";
                    break;
                default:
                    this._iconBg.background = "rgba(20, 20, 30, 0.7)";
                    this._iconBg.color = "rgba(255, 255, 255, 0.15)";
            }
        }

        if (this._iconImage) {
            this._iconImage.source = this._state === 'listening' ? AI_ICON_ACTIVE_SVG : AI_ICON_SVG;
        }

        // Status text for both the panel header and inline label
        let statusStr = "";
        let statusColor = "rgba(255, 255, 255, 0.5)";
        switch (this._state) {
            case 'listening':
                statusStr = "Listening...";
                statusColor = "rgba(255, 120, 120, 0.9)";
                break;
            case 'transcribing':
                statusStr = "Transcribing...";
                statusColor = "rgba(200, 180, 255, 0.9)";
                break;
            case 'thinking':
                statusStr = "Thinking...";
                statusColor = "rgba(120, 170, 255, 0.9)";
                break;
            case 'speaking':
                statusStr = "Speaking...";
                statusColor = "rgba(120, 255, 160, 0.9)";
                break;
            default:
                statusStr = "Tap icon to speak";
        }

        if (this._statusText) {
            this._statusText.text = statusStr;
            this._statusText.color = statusColor;
        }

        // Inline label: visible when active and panel is collapsed
        const showLabel = this._state !== 'idle' && !this._expanded;
        this._labelRoot.setEnabled(showLabel);
        if (this._labelText && showLabel) {
            this._labelText.text = statusStr;
            this._labelText.color = statusColor;
        }
    }

    // ── Conversation Display ──────────────────────────────────

    private _addMessage(role: 'user' | 'assistant', text: string): void {
        this._conversation.push({ role, text });

        if (!this._conversationStack) return;

        const bubble = new Rectangle(`msg-${this._conversation.length}`);
        bubble.width = 0.92;
        bubble.adaptHeightToChildren = true;
        bubble.paddingTop = "12px";
        bubble.paddingBottom = "12px";
        bubble.paddingLeft = "24px";
        bubble.paddingRight = "24px";
        bubble.cornerRadius = 24;
        bubble.thickness = 0;

        if (role === 'user') {
            bubble.background = "rgba(60, 100, 220, 0.35)";
            bubble.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
        } else {
            bubble.background = "rgba(255, 255, 255, 0.08)";
            bubble.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        }

        const label = new TextBlock();
        label.text = text;
        label.color = "white";
        label.fontSize = 48;
        label.fontFamily = "system-ui, -apple-system, sans-serif";
        label.textWrapping = TextWrapping.WordWrap;
        label.resizeToFit = true;
        label.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        label.paddingTop = "16px";
        label.paddingBottom = "16px";
        label.paddingLeft = "20px";
        label.paddingRight = "20px";
        bubble.addControl(label);

        this._conversationStack.addControl(bubble);

        if (this._scrollViewer) {
            this._scrollViewer.verticalBar.value = 1;
        }
    }
}
